import { CellContext } from '@tanstack/react-table';
import { LearnerProgress } from '.';
import styled, { useTheme } from 'styled-components';
import { TopicPill, TopicText } from '../TopicPill';
import { SmallPortalTooltip } from '../../Tooltip';
import { TooltipContent, TooltipDetail, TooltipTitle } from './Tooltip';

const PillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

export default function ActivityStatsCell(
  info: CellContext<LearnerProgress, unknown>
) {
  const theme = useTheme();
  const mentorTooltipId = `mentor-tooltip-${info.row.id}`;
  const generalTooltipId = `general-tooltip-${info.row.id}`;
  const onboardedAt = info.row.original.onboardedAt;

  if (!onboardedAt) {
    return '-';
  }

  const numAIQuestions =
    info.row.original.progress?.mentor?.numAIQuestions ?? null;
  const numAIHintInteractions =
    info.row.original.progress?.mentor?.numAIHintInteractions ?? null;
  const numAIWhatDoesThisMean =
    info.row.original.progress?.mentor?.numAIWhatDoesThisMean ?? null;
  const numMentorSOSRequests =
    info.row.original.progress?.mentor?.numMentorSOSQuestions ?? null;

  const numDaysActive =
    info.row.original.progress?.general?.numDaysActive ?? null;
  const numExercisesCompleted =
    info.row.original.progress?.general?.numExercisesCompleted ?? null;
  const totalExerciseTime =
    info.row.original.progress?.general?.totalExerciseTime ?? null;

  return (
    <PillsContainer>
      <TopicPill
        backgroundColor={theme.colors.bgDefault}
        data-tooltip-id={mentorTooltipId}
      >
        <TopicText>Mentor</TopicText>
      </TopicPill>
      <SmallPortalTooltip
        anchorSelect={`[data-tooltip-id="${mentorTooltipId}"]`}
      >
        <TooltipContent>
          {numMentorSOSRequests !== null && (
            <>
              <TooltipTitle># Mentor SOS Requests</TooltipTitle>
              <TooltipDetail>{numMentorSOSRequests}</TooltipDetail>
            </>
          )}

          {numAIQuestions !== null && (
            <>
              <TooltipTitle style={{ marginTop: '16px' }}>
                # AI questions
              </TooltipTitle>
              <TooltipDetail>{numAIQuestions}</TooltipDetail>
            </>
          )}

          {numAIHintInteractions !== null && (
            <>
              <TooltipTitle style={{ marginTop: '16px' }}>
                # AI hint interactions
              </TooltipTitle>
              <TooltipDetail>{numAIHintInteractions}</TooltipDetail>
            </>
          )}

          {numAIWhatDoesThisMean !== null && (
            <>
              <TooltipTitle style={{ marginTop: '16px' }}>
                # AI What does this mean?
              </TooltipTitle>
              <TooltipDetail>{numAIWhatDoesThisMean}</TooltipDetail>
            </>
          )}
        </TooltipContent>
      </SmallPortalTooltip>
      <TopicPill
        backgroundColor={theme.colors.bgDefault}
        data-tooltip-id={generalTooltipId}
      >
        <TopicText>General</TopicText>
      </TopicPill>
      <SmallPortalTooltip
        anchorSelect={`[data-tooltip-id="${generalTooltipId}"]`}
      >
        <TooltipContent>
          {numDaysActive !== null && (
            <>
              <TooltipTitle># Days Active</TooltipTitle>
              <TooltipDetail>{numDaysActive}</TooltipDetail>
            </>
          )}
          {numExercisesCompleted !== null && (
            <>
              <TooltipTitle style={{ marginTop: '16px' }}>
                # Exercises Completed
              </TooltipTitle>
              <TooltipDetail>{numExercisesCompleted}</TooltipDetail>
            </>
          )}
          {totalExerciseTime !== null && (
            <>
              <TooltipTitle style={{ marginTop: '16px' }}>
                Total Exercise Time
              </TooltipTitle>
              <TooltipDetail>{totalExerciseTime}</TooltipDetail>
            </>
          )}
        </TooltipContent>
      </SmallPortalTooltip>
    </PillsContainer>
  );
}
